import { Component, signal, computed, inject, HostListener, OnInit } from '@angular/core';
import { formatDate } from '../../core/services/locale-date.service';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { AuthService } from '../../core/auth/auth.service';
import { LayoutService } from '../../core/services/layout.service';
import { NotificationService } from '../../core/services/notification.service';
import { CrmApiService } from '../../features/crm/shared/crm-api.service';
import { environment } from '../../../environments/environment';
import { CompanyContextService } from '../../core/services/company-context.service';
import { InboxService, InboxItem } from '../../core/services/inbox.service';

interface SearchHit {
  id: string;
  type: 'contact' | 'company';
  label: string;
  sublabel: string;
  route: string[];
}

import { CrmStoreService } from '../../features/crm/shared/crm-store.service';

@Component({
  selector: 'sw-header',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss'
})
export class HeaderComponent implements OnInit {
  /** CRM module labels ("Companies" may be renamed, e.g. "Organizations"). */
  readonly store = inject(CrmStoreService);
  private readonly auth = inject(AuthService);
  private readonly layout = inject(LayoutService);
  private readonly notify = inject(NotificationService);
  /** Sidebar collapsed to its icons: the header shows the SiteWare mark instead of the full logo. */
  readonly sidebarCollapsed = this.layout.sidebarCollapsed;
  private readonly api = inject(CrmApiService);
  private readonly router = inject(Router);
  private readonly companyCtx = inject(CompanyContextService);

  // ── Notifications bell ──────────────────────────────────────────────────────
  readonly inbox = inject(InboxService);
  readonly bellOpen = signal(false);
  toggleBell() {
    this.bellOpen.update(o => !o);
    if (this.bellOpen()) this.inbox.refresh();
  }
  /** Open what the notification is about (e.g. the task) and mark it read. */
  openNotification(n: InboxItem) {
    this.inbox.markRead(n);
    this.bellOpen.set(false);
    if (n.action_url) this.router.navigateByUrl(n.action_url);
  }
  /** "5m ago", "3h ago", "2d ago", then the date. */
  timeAgo(iso: string): string {
    const t = new Date(iso.endsWith('Z') || /[+-]\d\d:\d\d$/.test(iso) ? iso : iso + 'Z').getTime();
    const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.round(hrs / 24);
    return days < 7 ? `${days}d ago` : formatDate(t);
  }

  /** Home target for the logo — /crm in production (dashboard is hidden there), /dashboard in dev. */
  readonly homeRoute = environment.landingRoute;

  // ── Company switcher ─────────────────────────────────────────────────────
  readonly selectedCompany = this.companyCtx.selected;
  readonly companies = this.companyCtx.companies;
  readonly companyMenuOpen = signal(false);
  readonly switchingCompany = signal(false);

  ngOnInit() {
    // Re-hydrate the company list after a hard refresh so the switcher stays visible
    // (in-memory signals reset on reload). Only when signed in.
    if (this.auth.isAuthenticated()) {
      this.companyCtx.ensureLoaded().catch(() => {});
    }
  }

  toggleCompanyMenu() {
    if (!this.companyMenuOpen()) this.companyCtx.ensureLoaded().catch(() => {});
    this.companyMenuOpen.update(v => !v);
  }

  async switchCompany(companyId: string) {
    if (companyId === this.selectedCompany()?.id) { this.companyMenuOpen.set(false); return; }
    this.switchingCompany.set(true);
    try {
      await this.companyCtx.select(companyId);
      this.companyMenuOpen.set(false);
      // Reload so every module re-fetches under the new company scope.
      this.router.navigateByUrl(environment.landingRoute).then(() => window.location.reload());
    } catch {
      this.notify.error('Could not switch company.');
    } finally {
      this.switchingCompany.set(false);
    }
  }

  readonly currentUser = this.auth.currentUser;
  /** Usable avatar image URL (presigned http link), or null to fall back to initials. */
  readonly avatarUrl = computed(() => {
    const url = this.currentUser()?.avatar_url;
    return url && url.startsWith('http') ? url : null;
  });
  readonly scrolled = signal(false);
  readonly userMenuOpen = signal(false);
  readonly searchFocused = signal(false);
  readonly searchQuery = signal('');

  // Global search across contacts + companies.
  readonly searchResults = signal<SearchHit[]>([]);
  readonly searchLoading = signal(false);
  readonly searchOpen = signal(false);
  readonly searchHasMore = signal(false);
  private readonly searchPageSize = 8;
  private searchTimer: ReturnType<typeof setTimeout> | null = null;

  @HostListener('window:scroll')
  onScroll() {
    this.scrolled.set(window.scrollY > 8);
    if (this.searchOpen()) this.searchOpen.set(false);
  }

  @HostListener('document:click', ['$event'])
  onDocClick(e: MouseEvent) {
    const target = e.target as HTMLElement;
    if (!target.closest('.user-menu-wrapper')) this.userMenuOpen.set(false);
    if (!target.closest('.notif-wrapper')) this.bellOpen.set(false);
    if (!target.closest('.company-switcher')) this.companyMenuOpen.set(false);
    if (!target.closest('.header-center')) this.searchOpen.set(false);
  }

  onSearchInput(value: string, ev?: Event) {
    // Text that arrives while the box isn't focused is the browser's autofill (e.g. a saved email on
    // Settings › Security), not the user typing: clear it and don't search.
    const el = ev?.target as HTMLInputElement | undefined;
    if (el && document.activeElement !== el) {
      el.value = this.searchQuery();
      return;
    }
    this.searchQuery.set(value);
    if (this.searchTimer) clearTimeout(this.searchTimer);
    const q = value.trim();
    if (!q) { this.searchResults.set([]); this.searchOpen.set(false); this.searchLoading.set(false); return; }
    this.searchOpen.set(true);
    this.searchLoading.set(true);
    this.searchTimer = setTimeout(() => this.runSearch(q), 300);
  }

  private async runSearch(q: string) {
    try {
      const [contactsRes, companiesRes] = await Promise.all([
        this.api.getContacts({ search: q, page: 1, pageSize: this.searchPageSize })
          .catch(() => ({ items: [], total_count: 0 } as any)),
        this.api.getCompanies({ search: q, page: 1, pageSize: this.searchPageSize })
          .catch(() => ({ items: [], total_count: 0 } as any)),
      ]);
      if (this.searchQuery().trim() !== q) return; // stale
      const contacts = contactsRes.items ?? [];
      const companies = companiesRes.items ?? [];
      const hits: SearchHit[] = [
        ...contacts.map((c: any) => {
          const name = c.full_name || `${c.first_name} ${c.last_name}`.trim();
          return { id: c.id, type: 'contact' as const, label: name,
            sublabel: c.email || c.job_title || c.company_name || this.store.termOne('contacts'), route: ['/crm/contacts', c.id] };
        }),
        ...companies.map((co: any) => ({ id: co.id, type: 'company' as const, label: co.name,
          sublabel: co.industry || co.website || this.store.termOne('companies'), route: ['/crm/companies', co.id] })),
      ];
      this.searchResults.set(hits);
      // Are there more matches than we showed? Surface a "see all" affordance if so.
      const contactTotal = contactsRes.totalCount ?? contacts.length;
      this.searchHasMore.set(contactTotal > contacts.length);
    } finally {
      if (this.searchQuery().trim() === q) this.searchLoading.set(false);
    }
  }

  goToResult(hit: SearchHit) {
    this.router.navigate(hit.route);
    this.closeSearch();
  }

  /** Jump to the full contacts list filtered by the current query. */
  seeAllContacts() {
    const q = this.searchQuery().trim();
    this.router.navigate(['/crm/contacts'], { queryParams: { search: q } });
    this.closeSearch();
  }

  private closeSearch() {
    this.searchOpen.set(false);
    this.searchQuery.set('');
    this.searchResults.set([]);
    this.searchHasMore.set(false);
  }

  /** The hamburger: collapses the sidebar on desktop; below 1024px (sidebar is a drawer) opens / closes it. */
  toggleMenu() {
    if (window.matchMedia('(max-width: 1024px)').matches) this.layout.toggleMobileDrawer();
    else this.layout.toggleSidebar();
  }
  toggleUserMenu() { this.userMenuOpen.update(v => !v); }

  logout() {
    this.auth.logout();
  }

  getInitials(name: string): string {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  }
}
